import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Table,
  InputGroup,
  InputGroupText,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Common/Breadcrumbs";
import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { CUSTOMER_RETURNS_PAGE } from "../../routes/routeConstants";
import { debounce, initial, set } from "lodash";

// Import actions from other slices
import { clientActions } from "../../sagas/clientSlice";
import { invoiceActions } from "../../sagas/invoiceSlice";
import { stockActions } from "../../sagas/stockSlice";
import { formatPrice } from "../../helpers/utility";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { productActions } from "../../sagas/productSlice";
import { FaBarcode, FaBox, FaBoxes, FaTag, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import { commercialActions } from "../../sagas/commercialSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";

const CustomerReturnForm = ({ customerReturn, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(customerReturn);
  console.log(customerReturn)
  const { currentReturn, newReturnNumber, loading } = useSelector((state) => state.customerReturns);
  const { list } = useSelector((state) => state.deliveryNotes);
  const [isSearching, setIsSearching] = useState(false);
  const { clients } = useSelector((state) => state.clients);
  const { invoices } = useSelector((state) => state.invoices);
  const [documentType, setDocumentType] = useState("NONE"); // NONE, FACTURE, BL
  const [chooseProductModal, setChooseProductModal] = useState(false);
  const [selectedInvoiceItems, setSelectedInvoiceItems] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unite
  const { list: commercialList, loading: commercialLoading } = useSelector((state) => state.commercials); // Adjust state path as needed
  const { entrepots } = useSelector(state => state.entrepots);

  const canBeUpdated = (isEditing && currentReturn?.statut === "BROUILLON") || !isEditing;
  const toggleChooseProductModal = () => setChooseProductModal(!chooseProductModal);
  const {
    products,
    loading: productLoading,
    totalPages,
    totalElements,
    pageSize,
  } = useSelector((status) => status.products);

  useEffect(() => {
    // Load clients and products
    dispatch(clientActions.search({
      query: ""
    }));

    if (isEditing) {
      dispatch(customerReturnActions.getById({ id: customerReturn.id }));
    }

  }, [dispatch, id, isEditing]);

  useEffect(() => {
    dispatch(productActions.findAllUnites());
    dispatch(customerReturnActions.generateNumero())
    dispatch(commercialActions.list({ query: '' })); // Fetch commercials on component mount
    dispatch(entrepotActions.list());
  }, []);

  useEffect(() => {
    dispatch(productActions.findAll({
      search: searchTerm,
      page: currentPage - 1,
      size: 10,
    }));

  }, [currentPage, chooseProductModal]);

  useEffect(() => {
    if (isEditing && currentReturn?.client?.id) {
      setSelectedClient(currentReturn.client.id);

      // Determine document type based on current return data
      if (currentReturn.factureOrigine) {
        setDocumentType("FACTURE");
        let payload = {
          query: clients.find(c => c.id === currentReturn.client.id)?.code || '',
        }
        dispatch(invoiceActions.list(payload));
      } else if (currentReturn.blOrigine) {
        setDocumentType("BL");
        let payload = {
          query: clients.find(c => c.id === currentReturn.client.id)?.code || '',
        }
        dispatch(deliveryActions.list(payload));
      } else {
        setDocumentType("NONE");
      }
    }
  }, [dispatch, isEditing, currentReturn]);

  const handleClientChange = (clientId, setFieldValue) => {
    setSelectedClient(clientId);
    setFieldValue("client", clientId);
    setFieldValue("factureOrigine", null);
    setFieldValue("blOrigine", null);
    setSelectedItems([]);

    // Reset document type when client changes
    setDocumentType("NONE");
    setFieldValue("documentType", "NONE");
  };

  const clientOptions = clients?.map(client => ({
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.nom} ${client.prenom || ''} `
      : client.entreprise,
  })) ?? [];

  // Handle modal search input change
  const handleModalSearchChange = (e) => {
    const value = e.target.value;
    setModalSearchTerm(value);
    debouncedModalSearch(value);
  };

  const debouncedModalSearch = useCallback(
    debounce((query) => {
      setIsSearching(true);
      dispatch(productActions.findAll({
        search: query,
        page: 0,
        size: pageSize,
        onSuccess: () => setIsSearching(false)
      }));
      setIsSearching(false);
    }, 1000), // Wait 1 second after user stops typing
    [modalSearchTerm]
  );

  const handleDocumentTypeChange = (type, setFieldValue) => {
    setDocumentType(type);
    setFieldValue("documentType", type);
    setFieldValue("factureOrigine", null);
    setFieldValue("blOrigine", null);
    setSelectedItems([]);

    // Si on change vers NONE, on peut laisser le client ou le mettre à null pour passager
    if (type !== "NONE") {
      // Pour FACTURE ou BL, un client est requis
      if (!selectedClient) {
        setFieldValue("client", null);
      } else if (type === "FACTURE") {
        let payload = {
          clientId: clients.find(c => c.id === selectedClient)?.id || '',
        }
        dispatch(invoiceActions.listWithSubInvoices(payload));
      } else if (type === "BL") {
        let payload = {
          clientId: clients.find(c => c.id === selectedClient)?.id || '',
        }
        dispatch(deliveryActions.list(payload));
      }
    }
  };

  const handleDocumentChange = (documentId, setFieldValue) => {
    if (documentType === "FACTURE") {
      setFieldValue("factureOrigine", documentId);
      setFieldValue("blOrigine", null);

      const selectedInvoice = invoices.find((invoice) => invoice.id === documentId);
      if (selectedInvoice && selectedInvoice.lignes) {
        setSelectedItems(selectedInvoice.lignes);
      } else {
        setSelectedItems([]);
      }
    } else if (documentType === "BL") {
      setFieldValue("blOrigine", documentId);
      setFieldValue("factureOrigine", null);

      const selectedBL = list.find((bl) => bl.id === documentId);
      if (selectedBL && selectedBL.lignes) {
        setSelectedItems(selectedBL.lignes);
      } else {
        setSelectedItems([]);
      }
    }

    setFieldValue("lignes", []);
  };

  const validationSchema = Yup.object().shape({
    dateOperation: Yup.date().required(t("customerReturns.form.validation.dateRequired")),
    client: Yup.number().when('documentType', {
      is: (val) => val === 'NONE',
      then: () => Yup.number().nullable(), // Client optionnel pour type NONE
      otherwise: () => Yup.number().required(t("customerReturns.form.validation.clientRequired"))
    }),
    documentType: Yup.string().required(t("customerReturns.form.validation.documentTypeRequired")),
    factureOrigine: Yup.number().when('documentType', {
      is: (val) => val === 'FACTURE',
      then: () => Yup.number().required(t("customerReturns.form.validation.invoiceRequired")),
      otherwise: () => Yup.number().nullable()
    }),
    blOrigine: Yup.number().when('documentType', {
      is: (val) => val === 'BL',
      then: () => Yup.number().required(t("customerReturns.form.validation.blRequired")),
      otherwise: () => Yup.number().nullable()
    }),
    motifRetour: Yup.string().required(t("customerReturns.form.validation.reasonRequired")),
    modeRemboursement: Yup.string().required(),
    lignes: Yup.array().min(1, t("customerReturns.form.validation.itemsRequired")),
  });


  const calculateTotals = (lines) => {
    let totalHT = 0;
    let totalTVA = 0;

    lines.forEach((line) => {
      const lineTotal = line.quantiteRetournee * line.prixUnitaireHT;
      const lineTVA = lineTotal * (line.tauxTVA);

      totalHT += lineTotal;
      totalTVA += lineTVA;
    });

    const totalTTC = totalHT + totalTVA;

    return {
      montantHT: totalHT,
      montantTVA: totalTVA,
      montantTTC: totalTTC,
    };
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const totals = calculateTotals(values.lignes);
    const dataToSubmit = {
      ...values,
      ...totals,
      isPassager: values.client ? false : true,
      factureOrigineId: values.factureOrigine,
      blOrigineId: values.blOrigine,
      clientId: values.client,
      commercialId: values.commercial,
    };

    if (isEditing) {
      dispatch(
        customerReturnActions.update({
          id: currentReturn?.id,
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.success'),
              text: t('customerReturns.messages.updateSuccess'),
            });
            onClose();
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('common.error'),
              text: t('customerReturns.messages.saveError'),
            });
            setSubmitting(false);
          },
        })
      );
    } else {
      dispatch(
        customerReturnActions.create({
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.success'),
              text: t('customerReturns.messages.updateSuccess'),
            });
            onClose();
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('common.error'),
              text: t('customerReturns.messages.saveError'),
            });
            setSubmitting(false);
          },
        })
      );
    }
  };

  const commercialOptions = commercialList?.map(com => ({
    value: com.id,
    label: com.fullName,
  })) ?? [];


  // Determine initial document type
  const getInitialDocumentType = () => {
    if (isEditing && currentReturn) {
      if (currentReturn.factureOrigine) return "FACTURE";
      if (currentReturn.blOrigine) return "BL";
    }
    return "NONE";
  };

  const initialValues = isEditing && currentReturn
    ? {
      numeroRetour: currentReturn.numeroRetour || "",
      dateOperation: currentReturn.dateOperation || new Date().toISOString().split("T")[0],
      client: currentReturn.client?.id || null,
      entrepotId: currentReturn.entrepotId,
      commercial: currentReturn.commercial?.id || null,
      documentType: getInitialDocumentType(),
      factureOrigine: currentReturn.factureOrigine?.id || null,
      blOrigine: currentReturn.blOrigine?.id || null,
      motifRetour: currentReturn.motifRetour || "",
      modeRemboursement: currentReturn.modeRemboursement || "AVOIR",
      isPassager: currentReturn.passager || false,
      lignes: currentReturn.lignes?.map(line => ({
        article: line.article?.id,
        articleId: line.article?.id,
        articleLibelle: line.article?.libelle,
        quantiteRetournee: line.quantiteRetournee,
        quantiteOriginale: line.quantiteOriginale,
        prixUnitaireHT: line.prixUnitaireHT,
        prixUnitaireTTC: line.prixUnitaireTTC,
        tauxTVA: line.tauxTVA,
        unite: unites.find(u => u.code === line.article.unite)?.libelle
      })) || [],
    }
    : {
      numeroRetour: newReturnNumber,
      dateOperation: new Date().toISOString().split("T")[0],
      client: null,
      entrepotId: null,
      documentType: "NONE",
      factureOrigine: null,
      blOrigine: null,
      motifRetour: "",
      modeRemboursement: "AVOIR",
      lignes: [],
    };


  if ((loading && isEditing)) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  const entrepotOptions = entrepots?.map(entrepot => ({
    value: entrepot.id,
    label: entrepot.code + ' - ' + entrepot.libelle,
  })) ?? [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title={t("customerReturns.title")}
            breadcrumbItem={
              isEditing
                ? t("customerReturns.form.editTitle")
                : t("customerReturns.form.newTitle")
            }
          />

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={12}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">
                          {t("customerReturns.form.generalInfo")}
                        </h4>
                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <label className="form-label">
                                {t("customerReturns.form.fields.returnNumber")}
                              </label>
                              <Field
                                name="numeroRetour"
                                type="text"
                                className="form-control"
                                placeholder={t("customerReturns.form.placeholders.returnNumber")}
                                disabled
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <label className="form-label">
                                {t("customerReturns.form.fields.returnDate")}
                              </label>
                              <Field
                                name="dateOperation"
                                type="date"
                                className={`form-control ${errors.dateOperation && touched.dateOperation ? "is-invalid" : ""
                                  }`}
                                disabled={!canBeUpdated}
                              />
                              <ErrorMessage
                                name="dateOperation"
                                component="div"
                                className="invalid-feedback"
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <label className="form-label">
                                {t("customerReturns.form.fields.refundMode")}
                              </label>
                              <Field
                                name="modeRemboursement"
                                as="select"
                                className="form-select"
                                disabled={!canBeUpdated}
                              >
                                <option value="AVOIR">
                                  {t("customerReturns.avoir")}
                                </option>
                              </Field>
                            </div>
                          </Col>
                          <Col md="3">
                            <Label htmlFor="entrepotId">{t('bonLivraison.entrepot')}:</Label>
                            <Select
                              id="entrepotId"
                              options={entrepotOptions}
                              value={entrepotOptions.find(option => option.value === values.entrepotId)}
                              onChange={(selectedOption) => setFieldValue('entrepotId', selectedOption.value)}
                              placeholder={t('bonLivraison.selectEntrepot')}
                              isSearchable
                              isDisabled={!canBeUpdated}
                            />
                            {errors.entrepotId && touched.entrepotId && (
                              <div className="text-danger small mt-1">{errors.entrepotId}</div>
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <Col md="4">
                            <Label htmlFor="commercialId">{t('delivery.commercial')}:</Label>
                            <Select
                              id="commercialId"
                              options={commercialOptions}
                              value={commercialOptions.find(option => option.value === values.commercial)}
                              onChange={(selectedOption) => {
                                setFieldValue('commercial', selectedOption.value);
                              }}
                              placeholder={t('delivery.selectCommercial')}
                              isSearchable
                              isDisabled={!canBeUpdated}
                              menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                              styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 })
                              }}
                            />
                            {errors.commercialId && touched.commercialId && (
                              <div className="text-danger small mt-1">{errors.commercialId}</div>
                            )}
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <label className="form-label">
                                {t("customerReturns.form.fields.client")}
                              </label>

                              <Select
                                value={clientOptions.find(option => option.value === values.client)}
                                onChange={(option) => handleClientChange(option.value, setFieldValue)}
                                options={clientOptions}
                                placeholder={t("customerReturns.form.placeholders.selectClient")}
                                isDisabled={!canBeUpdated}
                                isSearchable
                                className={
                                  errors.client && touched.client ? "is-invalid" : ""
                                }
                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                styles={{
                                  menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                              />

                              {errors.client && touched.client && (
                                <div className="invalid-feedback d-block">
                                  {errors.client}
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <div className="mb-3">
                              <label className="form-label">
                                {t("customerReturns.form.fields.reason")}
                              </label>
                              <Field
                                name="motifRetour"
                                as="textarea"
                                rows="3"
                                className={`form-control ${errors.motifRetour && touched.motifRetour ? "is-invalid" : ""
                                  }`}
                                placeholder={t("customerReturns.form.placeholders.reason")}
                                disabled={!canBeUpdated}
                              />
                              <ErrorMessage
                                name="motifRetour"
                                component="div"
                                className="invalid-feedback"
                              />
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col lg={12}>
                    <Card>
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h4 className="card-title">
                            {t("customerReturns.form.returnedItems")}
                          </h4>
                        </div>

                        {selectedItems.length > 0 && (
                          <div className="mb-4">
                            <h5>{t("customerReturns.form.invoiceItems")}</h5>
                            <div className="table-responsive">
                              <Table className="table-centered table-nowrap mb-0">
                                <thead>
                                  <tr>
                                    <th>{t("customerReturns.form.columns.item")}</th>
                                    <th>{t("customerReturns.form.columns.quantity")}</th>
                                    <th>{t("customerReturns.form.columns.unite")}</th>
                                    <th>{t("customerReturns.form.columns.unitPrice")}</th>
                                    <th>{t("customerReturns.form.columns.total")}</th>
                                    <th>{t("customerReturns.form.columns.actions")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedItems.map((item, index) => (
                                    <tr key={index}>
                                      <td>{item.articleNom}</td>
                                      <td>{item.quantite ?? item.quantiteLivree}</td>
                                      <td>{unites.find(u => u.code === item.unite)?.libelle}</td>
                                      <td>{formatPrice(item.prixUnitaireTTC)}</td>
                                      <td>{formatPrice(item.montantTTC)}</td>
                                      <td>
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => {
                                            // Check if the item is already in the return lines
                                            const isItemAlreadyAdded = values.lignes.some(
                                              line => line.article === item.articleId || line.articleId === item.articleId
                                            );

                                            if (isItemAlreadyAdded) {
                                              // Show a warning toast
                                              toast.warning(t("customerReturns.messages.itemAlreadyAdded"));
                                            } else {
                                              // Add the item to return lines
                                              const newLine = {
                                                articleId: item.articleId,
                                                article: item.articleId, // Keep both for compatibility
                                                articleLibelle: item.articleNom,
                                                quantiteRetournee: 1,
                                                quantiteOriginale: item.quantite || item.quantiteLivree,
                                                prixUnitaireHT: item.prixUnitaireHT,
                                                prixUnitaireTTC: item.prixUnitaireTTC,
                                                tauxTVA: item.tauxTVA,
                                                unite: item.unite,
                                              };
                                              setFieldValue("lignes", [...values.lignes, newLine]);
                                            }
                                          }}
                                          disabled={values.lignes.some(
                                            line => line.article === item.articleId || line.articleId === item.articleId
                                          )}
                                        >
                                          <i className="bx bx-plus"></i>{" "}
                                          {values.lignes.some(
                                            line => line.article === item.articleId || line.articleId === item.articleId
                                          )
                                            ? t("customerReturns.form.buttons.alreadyAdded")
                                            : t("customerReturns.form.buttons.add")}
                                        </Button>

                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Manual item addition section for when no document is selected */}
                        {documentType === "NONE" && (
                          <div className="mb-4">
                            <h5>{t("customerReturns.addManualItem")}</h5>
                            <Row>
                              <Col md={12}>
                                <div className="mb-3">
                                  <Button
                                    color="primary"
                                    onClick={toggleChooseProductModal}
                                    disabled={!canBeUpdated}
                                  >
                                    <i className="bx bx-plus me-1"></i>
                                    {t("customerReturns.selectProduct")}
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )}
                        <h5>{t("customerReturns.form.returnedItems")}</h5>
                        <FieldArray name="lignes">
                          {({ remove, push }) => (
                            <div className="table-responsive">
                              <Table className="table-centered table-nowrap mb-0">
                                <thead>
                                  <tr>
                                    <th>{t("customerReturns.form.columns.item")}</th>
                                    <th>{t("customerReturns.form.columns.returnedQte")}</th>
                                    <th>{t("customerReturns.form.columns.unite")}</th>
                                    <th>{t("customerReturns.form.columns.unitPrice")}</th>
                                    <th>{t("customerReturns.form.columns.total")}</th>
                                    <th>{t("customerReturns.form.columns.actions")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {values.lignes.length === 0 ? (
                                    <tr>
                                      <td colSpan="6" className="text-center">
                                        {t("customerReturns.form.noItems")}
                                      </td>
                                    </tr>
                                  ) : (
                                    values.lignes.map((line, index) => {
                                      const lineTotal = line.quantiteRetournee * line.prixUnitaireTTC;
                                      return (
                                        <tr key={index}>
                                          <td>
                                            {line.articleLibelle || "Article inconnu"}
                                          </td>
                                          <td>
                                            <Field
                                              name={`lignes.${index}.quantiteRetournee`}
                                              type="number"
                                              onFocus={(e) => e.target.select()} // Select the field content on focus
                                              min="1"
                                              step="1"
                                              className="form-control"
                                              style={{ width: "80px" }}
                                              disabled={!canBeUpdated}
                                            />
                                          </td>
                                          <td>
                                            {unites.find(u => u.code === line.unite)?.libelle || line.unite}
                                          </td>
                                          <td>
                                            <InputGroup size="sm">
                                              <Field
                                                name={`lignes.${index}.prixUnitaireTTC`}
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="form-control"
                                                style={{ width: "100px" }}
                                                disabled
                                              />
                                              <InputGroupText>DH</InputGroupText>
                                            </InputGroup>
                                          </td>
                                          <td>{formatPrice(lineTotal)}</td>
                                          <td>
                                            <Button
                                              color="danger"
                                              size="sm"
                                              onClick={() => remove(index)}
                                              disabled={!canBeUpdated}
                                            >
                                              <FaTrash />
                                            </Button>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  )}
                                </tbody>
                                <tfoot>
                                  {values.lignes.length > 0 && (
                                    <>
                                      <tr>
                                        <th colSpan="4" className="text-end">
                                          {t("customerReturns.form.totals.totalHT")}:
                                        </th>
                                        <th>
                                          {formatPrice(
                                            calculateTotals(values.lignes).montantHT
                                          )}
                                        </th>
                                        <th></th>
                                      </tr>
                                      <tr>
                                        <th colSpan="4" className="text-end">
                                          {t("customerReturns.form.totals.totalVAT")}:
                                        </th>
                                        <th>
                                          {formatPrice(
                                            calculateTotals(values.lignes).montantTVA
                                          )}
                                        </th>
                                        <th></th>
                                      </tr>
                                      <tr>
                                        <th colSpan="4" className="text-end">
                                          {t("customerReturns.form.totals.totalTTC")}:
                                        </th>
                                        <th>
                                          {formatPrice(
                                            calculateTotals(values.lignes).montantTTC
                                          )}
                                        </th>
                                        <th></th>
                                      </tr>
                                    </>
                                  )}
                                </tfoot>
                              </Table>
                            </div>
                          )}
                        </FieldArray>
                        {errors.lignes && typeof errors.lignes === "string" && (
                          <div className="text-danger mt-2">{errors.lignes}</div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        color="secondary"
                        onClick={onClose}
                      >
                        {t("customerReturns.form.buttons.cancel")}
                      </Button>
                      <Button color="primary" type="submit" disabled={isSubmitting || !canBeUpdated}>
                        {isSubmitting ? (
                          <>
                            <Spinner size="sm" className="me-2" />{" "}
                            {t("customerReturns.form.buttons.saving")}
                          </>
                        ) : (
                          t("customerReturns.form.buttons.save")
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Modal isOpen={chooseProductModal} toggle={toggleChooseProductModal} size="xl">
                  <ModalHeader toggle={toggleChooseProductModal}>
                    {t('product.chooseProduct')}
                  </ModalHeader>
                  <ModalBody>
                    <Row>
                      <Col md={12}>
                        <Input
                          type="text"
                          placeholder={t('product.searchPlaceholder')}
                          value={modalSearchTerm}
                          onChange={handleModalSearchChange}
                          className="me-2" // Added margin to the right
                        />
                      </Col>

                    </Row>
                    <>
                      <div className="cart-container" style={{
                        height: '300px',
                        overflowY: 'auto'
                      }}>

                        <Table hover bordered className="align-middle mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th className="align-middle">
                                <FaBarcode className="me-2 text-primary" size={12} />
                                {t('product.code')}
                              </th>
                              <th className="align-middle">
                                <FaBox className="me-2 text-primary" size={12} />
                                {t('product.description')}
                              </th>
                              <th className="align-middle text-end">
                                <FaTag className="me-2 text-primary" size={12} />
                                {t('product.salePrice')}
                              </th>
                              {/* <th className="align-middle text-end">
                                  <FaTag className="me-2 text-primary" size={12} />
                                  {t('product.buyingPrice')}
                              </th> */}
                              <th className="align-middle text-center">
                                <FaBoxes className="me-2 text-primary" size={12} />
                                {t('product.stockPhysique')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {!products || products.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <div className="text-muted">
                                    {t('product.noProducts')}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              products.map((product) => (
                                <tr
                                  key={product.id}
                                  onClick={() => {
                                    // Check if the product is already in the return lines
                                    const isProductAlreadyAdded = values.lignes.some(
                                      line => (line.article === product.id || line.articleId === product.id)
                                    );

                                    if (isProductAlreadyAdded) {
                                      // Show a warning toast
                                      toast.warning(t("customerReturns.messages.itemAlreadyAdded"));
                                    } else {
                                      // Add product to return lines
                                      const newLine = {
                                        article: product.id,
                                        articleId: product.id, // Keep both for compatibility
                                        articleLibelle: product.libelle,
                                        quantiteRetournee: 1,
                                        quantiteOriginale: 1,
                                        prixUnitaireHT: product.prixVenteHT || (product.prixVenteTTC1 / (1 + (product.tauxTVA || 0.2))),
                                        prixUnitaireTTC: product.prixVenteTTC1,
                                        tauxTVA: product.tauxTVA || 0.2,
                                        unite: unites.find(u => u.code === product.unite)?.libelle,
                                      };
                                      setFieldValue("lignes", [...values.lignes, newLine]);
                                      toggleChooseProductModal();
                                    }
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    backgroundColor: values.lignes.some(
                                      line => (line.article === product.id || line.articleId === product.id)
                                    ) ? '#f8f9fa' : 'inherit'
                                  }}
                                  className={values.lignes.some(
                                    line => (line.article === product.id || line.articleId === product.id)
                                  ) ? 'text-muted' : ''}
                                >
                                  <td className="align-middle">{product.code}</td>
                                  <td className="align-middle">
                                    {product.libelle.length > 30 ? product.libelle.substring(0, 30) + '...' : product.libelle}
                                    {values.lignes.some(
                                      line => (line.article === product.id || line.articleId === product.id)
                                    ) && (
                                        <span className="ms-2 badge bg-light text-dark">
                                          {t("customerReturns.form.alreadyAdded")}
                                        </span>
                                      )}
                                  </td>
                                  <td className="align-middle text-end">
                                    {formatPrice(product.prixVenteTTC1)}
                                  </td>
                                  <td className="align-middle text-center">{product.stockPhysique}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          {t('product.showing', {
                            start: (currentPage - 1) * pageSize + 1,
                            end: Math.min(currentPage * pageSize, totalElements),
                            total: totalElements,
                            currentPage: currentPage,
                            totalPages: totalPages
                          })}
                        </small>
                      </div>
                    </>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={() => {
                      setModalSearchTerm('');
                      setCurrentPage(1);
                      toggleChooseProductModal()
                    }}>
                      {t('common.cancel')}
                    </Button>
                  </ModalFooter>
                </Modal>
              </Form>
            )}
          </Formik>
        </Container>
      </div>

    </React.Fragment>
  );
};

export default CustomerReturnForm;


